/**
 * A `/match_events` node on the transcript: the submit the interviewer actually
 * made, and the matcher's answer coming back.
 *
 * The node body is shown as STORED, never decoded. On a `source:'reasoner'`
 * request `extracted` is RTDB-keyed (`them:name`, not `them.name` —
 * F-CONTRACT-FIX §3.5) because RTDB refuses a dot in a key; decoding it with
 * `contract/extracted_v1.rtdb-keys.json` is PD-4's job on the match-events page.
 * Re-keying it here would put bytes on the screen that are in no store.
 */
import { Badge, RawJson, type Tone } from './ui';
import { formatClock, type EventRow as EventRowModel } from './trace';

const REQUEST_TONE: Record<string, Tone> = {
  pending: 'amber',
  processing: 'blue',
  done: 'green',
  failed: 'red',
  error: 'red',
};

export default function EventRow({ row }: { row: EventRowModel }) {
  const e = row.event;
  const isRequest = e.side === 'request';
  return (
    <div className="flex justify-center" data-testid="event-row">
      <div className="w-full md:w-11/12 rounded-md border border-dashed border-blue-300 bg-blue-50/50 p-2">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge tone={isRequest ? 'purple' : 'blue'}>{isRequest ? 'submit → matcher' : 'matcher → pebble'}</Badge>
          <Badge tone={isRequest ? (REQUEST_TONE[e.label] ?? 'gray') : 'gray'}>{e.label}</Badge>
          {e.storyVersion !== null && <span className="text-gray-600">v{e.storyVersion}</span>}
          {e.source && <Badge tone="gray">{e.source}</Badge>}
          {e.clientEvent && <Badge tone="gray">{e.clientEvent}</Badge>}
          {e.mustTellUser === true && <Badge tone="amber">must tell user</Badge>}
          {e.attempts !== null && e.attempts > 1 && <Badge tone="amber">attempt {e.attempts}</Badge>}
          <span className="text-gray-500">
            {formatClock(e.ts)}
            {row.undated ? ' (no timestamp — placed last)' : ''}
          </span>
        </div>
        <div className="mt-1 text-xs text-gray-500 font-mono break-all">
          {e.key}
          {e.requestKey ? ` ← ${e.requestKey}` : ''}
          {e.matchId ? ` · match ${e.matchId}` : ''}
        </div>
        {e.error && <div className="mt-1 text-xs text-red-700">{e.error}</div>}
        <RawJson value={e.node} label={`raw ${e.side} node (as stored)`} />
      </div>
    </div>
  );
}
